// src/services/guestSession.js — the GUEST's side of a link.
//
// Someone with no Allo account opens /g/<token>. By the end of this file they
// are a first-class participant in one conversation: they have a keypair, a
// published device, a uid the rules can bind to, and a chat that every existing
// message path already knows how to talk to.
//
// ══ THREE IDENTITIES, AND WHY EACH ONE IS NEEDED ═══════════════════════════
//   1. A FIREBASE ANONYMOUS UID. Firestore rules can only reason about
//      `request.auth.uid`, so a uid we invent is unenforceable — anyone could
//      claim it. The anonymous sign-in gives the guest a real, provable one.
//   2. A DEVICE KEYPAIR, generated in the browser exactly as the web app
//      generates its own, and published at users/{guestUid}.devices. This is
//      what makes `sealForUsers([owner, guest])` work with no special case
//      anywhere: to the phone, the guest is just another device to seal to.
//   3. A LOCAL SESSION RECORD so returning to the link — from the email, an
//      hour later, on the same device — resumes rather than restarts.
//
// 🟥 THE KEYPAIR IS THE HISTORY. Because messages are sealed to it, clearing
//    this browser's storage or opening the link on a SECOND device produces a
//    new key that old messages were never sealed to — those messages become
//    unreadable for the guest. That is the honest cost of end-to-end encryption
//    for someone with no account to sync from, and the join screen says so.

import { signInAnonymously, onAuthStateChanged, signOut } from 'firebase/auth';
import {
  ref as rtdbRef, set as rtdbSet, onDisconnect, serverTimestamp as rtServerTimestamp,
} from 'firebase/database';
import { auth, rtdb } from '../config/firebase';
import { getIdentity, registerDevice, stopSeenTimer } from '../crypto/identity';
import { BACKEND_URL } from './socket';

const SESSION_KEY = 'allo:guest-session';

/** What we remember locally about this guest's link. */
export function loadGuestSession() {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || 'null'); } catch { return null; }
}
export function saveGuestSession(s) {
  try { localStorage.setItem(SESSION_KEY, JSON.stringify(s)); } catch {}
}
export function clearGuestSession() {
  try { localStorage.removeItem(SESSION_KEY); } catch {}
}
/** 🔗 THE LINK HAS ENDED — everything this tab was doing as the guest stops. The session was
 *  forgotten, but the anonymous sign-in stayed and the device check kept re-writing a device entry
 *  every few hours under an account the server had deleted. Now: the check stops and the guest
 *  signs out (the server deletes the sign-in itself as it purges — guestLinks purgeGuestChat). */
export async function endGuestSession() {
  clearGuestSession();
  stopSeenTimer();
  try { if (auth.currentUser && auth.currentUser.isAnonymous) await signOut(auth); } catch { /* already gone */ }
}

/** The token in /g/<token>, or null if this is not a guest URL. */
export function guestTokenFromLocation() {
  try {
    const m = window.location.pathname.match(/^\/g\/([A-Za-z0-9_-]{8,})\/?$/);
    return m ? m[1] : null;
  } catch { return null; }
}

async function post(path, body) {
  const res = await fetch(`${BACKEND_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false) {
    const err = new Error(json.error || `http_${res.status}`);
    err.code = json.error || `http_${res.status}`;
    throw err;
  }
  return json;
}

/** What the join screen shows before anyone has identified themselves. */
export function peekGuestLink(token) {
  return post('/api/guest/peek', { token }).then((r) => r.link);
}

/**
 * Sign in anonymously — once per browser, reused forever after.
 *
 * 🟢 AWAITS THE AUTH STATE, not just the promise. `signInAnonymously` resolves
 *    before the SDK has finished installing the user everywhere, and a
 *    Firestore listener attached in that gap runs unauthenticated and is denied
 *    by rules. Waiting for the state change is the difference between a chat
 *    that loads and a chat that is permanently empty.
 */
export async function ensureAnonUser() {
  /* 🟥 NEVER REUSE A REAL ACCOUNT, and never sign in over one.
     This used to be `if (auth.currentUser) return auth.currentUser`, which is
     wrong in both directions once you remember that Firebase Auth persistence is
     PER ORIGIN and shared by every tab:

       · If an Allo account was signed in, the guest page adopted it. /join then
         bound the "guest" to the owner's own uid — stamping isGuest onto their
         real user document and creating a chat with themselves.

       · And if it had signed in anonymously anyway, that would have replaced the
         Allo session for the whole origin — every other tab included. The web
         app would quietly find itself logged in as the guest, its chat list
         empty and its history sync waiting on a phone that is watching a
         different uid entirely.

     Neither is recoverable from in here, so the page refuses instead — see
     `guestSessionConflict()` and the screen it drives. */
  const cur = auth.currentUser;
  if (cur && !cur.isAnonymous) throw Object.assign(new Error('signed_in_here'), { code: 'signed_in_here' });
  if (cur) return cur;
  await signInAnonymously(auth);
  return new Promise((resolve) => {
    const off = onAuthStateChanged(auth, (u) => {
      if (u) { off(); resolve(u); }
    });
  });
}

/**
 * Is a real Allo account signed in on this origin right now?
 *
 * 🟢 ASKED BEFORE ANYTHING ELSE HAPPENS, because the damage is done the moment
 *    we touch auth. Resolves once the SDK has finished restoring whatever
 *    session it had — `auth.currentUser` is null for a beat on a cold load, and
 *    reading it too early would report "nobody is signed in" for a browser that
 *    very much is.
 */
export function guestSessionConflict() {
  return new Promise((resolve) => {
    const off = onAuthStateChanged(auth, (u) => {
      off();
      resolve(!!(u && !u.isAnonymous));
    });
  });
}

/**
 * Join (or re-join) the link.
 * Publishes this browser's device key and returns everything the chat needs.
 */
export async function joinGuestLink({ token, passcode, name, email, photoUrl }) {
  const user = await ensureAnonUser();
  const idToken = await user.getIdToken();
  const id = await getIdentity();          // creates the keypair on first run

  const r = await post('/api/guest/join', {
    idToken,
    token,
    passcode: passcode || '',
    name,
    email,
    photoUrl: photoUrl || null,
    deviceId: id.deviceId,
    publicKey: id.publicKey,
  });

  const session = {
    token,
    chatId: r.chatId,
    guestUid: r.guestUid,
    ownerUid: r.ownerUid,
    ownerName: r.ownerName,
    ownerImage: r.ownerImage,
    expiresAt: r.expiresAt,
    me: r.guest,
  };
  saveGuestSession(session);
  return session;
}

/**
 * ⭐ Join a CALL link. One phone call with somebody who does not have Allo.
 *
 * 🟥 A DIFFERENT ROUTE, NOT A FLAG ON joinGuestLink. /api/guest/join builds a
 *    conversation — a chat document, a system message, an unread bump and a push
 *    telling the owner somebody arrived. A call link promises none of that, and
 *    the owner asked for a phone call rather than a thread that outlives it.
 *
 * 🟢 THE DEVICE KEY IS STILL PUBLISHED even though call media is encrypted by
 *    DTLS-SRTP and never touches Allo's message keys. `devices.{deviceId}` is
 *    what per-device call routing addresses: ring dismissal, "answered
 *    elsewhere", and pending-ICE delivery all look this browser up by it. A
 *    guest without one silently degrades to "newest socket of the account".
 *
 * 🟥 NOTHING IS SAVED TO localStorage. A chat session is resumed an hour later
 *    from the email; a call is a single live moment, and a "session" pointing at
 *    a call that ended when the tab closed is a resume into nothing. Refreshing
 *    re-joins — the server explicitly allows the SAME guest back onto a link it
 *    has already claimed, so a dropped tab mid-setup is recoverable.
 */
export async function joinGuestCallLink({ token, passcode, name, email, photoUrl, callType }) {
  const user = await ensureAnonUser();
  const idToken = await user.getIdToken();
  const id = await getIdentity();          // creates the keypair on first run

  const r = await post('/api/guest/call/join', {
    idToken,
    token,
    passcode: passcode || '',
    name,
    email,
    photoUrl: photoUrl || null,
    callType: callType === 'video' ? 'video' : 'voice',
    deviceId: id.deviceId,
    publicKey: id.publicKey,
  });

  return {
    kind: 'call',
    token,
    guestUid: r.guestUid,
    ownerUid: r.ownerUid,
    ownerName: r.ownerName,
    ownerImage: r.ownerImage,
    expiresAt: r.expiresAt,
    callType: r.callType === 'video' ? 'video' : 'voice',
    deviceId: id.deviceId,
    me: r.guest,
  };
}

/**
 * 🟥 THE STEP THAT MAKES SENDING WORK AT ALL, and the one the guest route had no
 *    way to reach.
 *
 *    `sealForUsers()` calls `ensureKeyPublished()` before every message, and that
 *    function returns FALSE unless a module-level uid was set — which only
 *    `registerDevice(uid)` does, and which App.jsx calls behind its auth gate.
 *    GuestApp renders on /g/<token>, OUTSIDE that gate, so the uid stayed null,
 *    every seal threw "this browser isn't fully linked yet", and the send button
 *    looked simply dead: the draft came back, nothing else happened.
 *
 *    The server already published this browser's key at /join, so the common case
 *    is a single read that confirms it and returns. It is called on both paths —
 *    a fresh join AND a resumed session — because a returning guest never touches
 *    the join code again.
 *
 * @returns {Promise<boolean>} whether the key is confirmed readable by others.
 */
export async function activateGuestIdentity(session) {
  if (!session || !session.guestUid) return false;
  try {
    await ensureAnonUser();          // the SDK must hold the user before any write
    await registerDevice(session.guestUid);
    return true;
  } catch (e) {
    console.warn('[guest] could not activate this browser identity:', e && e.message);
    return false;
  }
}

/**
 * Publish the guest's own presence, so the OWNER sees "online" and a real last
 * seen instead of nothing at all.
 *
 * 🟢 THE SHAPE IS THE PHONE'S SHAPE, deliberately: `{ online, lastSeen }` under
 *    status/<uid>, with an onDisconnect that flips it — byte for byte what
 *    HomeScreen.tsx writes. watchPresence() on the other side then needs no
 *    guest-specific case, and neither does anything else that reads presence.
 *
 * 🟥 onDisconnect IS THE WHOLE POINT. A browser tab is closed, not logged out;
 *    without a server-side disconnect hook the guest would read as permanently
 *    online, which is worse than showing nothing. It is armed BEFORE the online
 *    write, so a connection that drops in between still resolves to offline.
 */
export function startGuestPresence(guestUid) {
  if (!guestUid) return () => {};
  let stopped = false;
  const r = rtdbRef(rtdb, `status/${guestUid}`);
  const offline = { online: false, lastSeen: rtServerTimestamp() };

  const goOnline = () => {
    if (stopped) return;
    onDisconnect(r).set(offline)
      .then(() => rtdbSet(r, { online: true, lastSeen: null }))
      .catch((e) => console.warn('[guest] presence write refused:', e && e.message));
  };

  goOnline();
  // a tab that is hidden for a while and comes back has usually lost the socket
  const onVis = () => { if (document.visibilityState === 'visible') goOnline(); };
  document.addEventListener('visibilitychange', onVis);
  window.addEventListener('focus', goOnline);

  return () => {
    stopped = true;
    document.removeEventListener('visibilitychange', onVis);
    window.removeEventListener('focus', goOnline);
    // leaving on purpose: say so now rather than waiting for the socket to lapse
    rtdbSet(r, offline).catch(() => {});
  };
}

/**
 * "I am here." Drives the owner's presence line and, more importantly, the
 * email digest: a guest who is reading right now must not be emailed about the
 * messages in front of them.
 *
 * Returns the link's live expiry so the page can lock itself the moment it ends
 * rather than at the next reload.
 */
export async function pingGuest(session) {
  if (!session || !session.token) return null;
  try {
    const user = auth.currentUser || await ensureAnonUser();
    const idToken = await user.getIdToken();
    return await post('/api/guest/ping', { idToken, token: session.token });
  } catch { return null; }
}

/** Start the heartbeat: on focus, on visibility, and every 45s while open. */
export function startGuestHeartbeat(session, onState) {
  let stopped = false;
  const beat = async () => {
    if (stopped) return;
    const r = await pingGuest(session);
    if (r && onState) onState(r);
  };
  beat();
  const iv = setInterval(beat, 45000);
  const onVis = () => { if (document.visibilityState === 'visible') beat(); };
  window.addEventListener('focus', beat);
  document.addEventListener('visibilitychange', onVis);
  return () => {
    stopped = true;
    clearInterval(iv);
    window.removeEventListener('focus', beat);
    document.removeEventListener('visibilitychange', onVis);
  };
}
